import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Star, X, Upload, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { motion, AnimatePresence } from 'framer-motion';

const reviewSchema = z.object({
  name: z.string().min(2, 'Name is required'),
  rating: z.number().min(1, 'Please select a rating').max(5),
  review_text: z.string().min(10, 'Review must be at least 10 characters'),
});

type ReviewFormValues = z.infer<typeof reviewSchema>;

interface ReviewFormProps {
  onClose: () => void;
  onSuccess: () => void;
}

export const ReviewForm = ({ onClose, onSuccess }: ReviewFormProps) => {
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [images, setImages] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { register, handleSubmit, setValue, formState: { errors } } = useForm<ReviewFormValues>({
    resolver: zodResolver(reviewSchema),
    defaultValues: {
      rating: 0,
    }
  });

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      const validFiles = newFiles.filter(file => {
        const isValidType = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(file.type);
        const isValidSize = file.size <= 5 * 1024 * 1024; // 5MB
        return isValidType && isValidSize;
      });

      if (validFiles.length < newFiles.length) {
        alert('Some files were skipped. Max size is 5MB and only images (JPG, PNG, WEBP) are allowed.');
      }

      setImages(prev => [...prev, ...validFiles].slice(0, 8));
      const newPreviews = validFiles.map(file => URL.createObjectURL(file));
      setPreviews(prev => [...prev, ...newPreviews].slice(0, 8));
    }
  };

  const removeImage = (index: number) => {
    setImages(prev => prev.filter((_, i) => i !== index));
    setPreviews(prev => {
      URL.revokeObjectURL(prev[index]!);
      return prev.filter((_, i) => i !== index);
    });
  };

  const onSubmit = async (data: ReviewFormValues) => {
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const photoUrls: string[] = [];

      for (const image of images) {
        const fileExt = image.name.split('.').pop();
        const fileName = `${crypto.randomUUID()}.${fileExt}`;
        const filePath = `${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('review-photos')
          .upload(filePath, image);

        if (uploadError) throw new Error(`Upload failed: ${uploadError.message}`);

        const { data: { publicUrl } } = supabase.storage
          .from('review-photos')
          .getPublicUrl(filePath);
        
        photoUrls.push(publicUrl);
      }

      const { error } = await supabase.from('reviews').insert({
        name: data.name.trim(),
        rating: data.rating,
        review_text: data.review_text.trim(),
        photos: photoUrls,
        status: 'pending'
      });

      if (error) throw new Error(`Database error: ${error.message}`);

      setIsSubmitted(true);
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 3000);
    } catch (error: any) {
      console.error('Error submitting review:', error);
      setSubmitError(error.message || 'Failed to submit review. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="bg-ivory p-8 rounded-2xl max-w-2xl w-full mx-auto text-center py-16">
        <motion.div
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
        >
          <CheckCircle2 className="w-20 h-20 text-emerald mx-auto mb-6" />
          <h3 className="text-3xl font-serif text-emerald mb-4">Thank you for sharing your experience!</h3>
          <p className="text-[#59635C] font-clean text-lg">Your review has been submitted and is awaiting approval.</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="bg-ivory p-6 md:p-8 rounded-2xl max-w-2xl w-full mx-auto relative overflow-y-auto max-h-[90vh] shadow-2xl">
      <button 
        onClick={onClose}
        type="button"
        className="absolute top-4 right-4 p-2 hover:bg-black/5 rounded-full transition-colors z-10"
      >
        <X className="w-6 h-6 text-[#59635C]" />
      </button>

      <h2 className="text-3xl font-serif text-emerald mb-8">Write a Review</h2>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div>
          <label className="block text-sm font-bold text-emerald uppercase tracking-wider mb-2 font-clean">
            Full Name *
          </label>
          <input
            {...register('name')}
            className="w-full px-4 py-3 rounded-lg border border-accent/20 focus:border-accent outline-none font-clean bg-white/50 text-[#176B5B] placeholder-[#176B5B]/30"
            placeholder="Enter your name"
          />
          {errors.name && <p className="text-red-500 text-xs mt-1 font-clean">{errors.name.message}</p>}
        </div>

        <div>
          <label className="block text-sm font-bold text-emerald uppercase tracking-wider mb-2 font-clean">
            Star Rating *
          </label>
          <div className="flex gap-2">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => {
                  setRating(star);
                  setValue('rating', star);
                }}
                onMouseEnter={() => setHoveredRating(star)}
                onMouseLeave={() => setHoveredRating(0)}
                className="transition-transform hover:scale-110"
              >
                <Star
                  className={`w-8 h-8 ${
                    (hoveredRating || rating) >= star ? 'fill-accent text-accent' : 'text-gray-300'
                  }`}
                />
              </button>
            ))}
          </div>
          {errors.rating && <p className="text-red-500 text-xs mt-1 font-clean">{errors.rating.message}</p>}
        </div>

        <div>
          <label className="block text-sm font-bold text-emerald uppercase tracking-wider mb-2 font-clean">
            Your Experience *
          </label>
          <textarea
            {...register('review_text')}
            rows={4}
            className="w-full px-4 py-3 rounded-lg border border-accent/20 focus:border-accent outline-none font-clean bg-white/50 resize-none text-[#176B5B] placeholder-[#176B5B]/30"
            placeholder="Tell us about your stay..."
          />
          {errors.review_text && <p className="text-red-500 text-xs mt-1 font-clean">{errors.review_text.message}</p>}
        </div>

        <div>
          <label className="block text-sm font-bold text-emerald uppercase tracking-wider mb-2 font-clean">
            Add Photos (Optional)
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
            {previews.map((preview, index) => (
              <div key={index} className="relative aspect-square rounded-lg overflow-hidden group">
                <img src={preview} alt="Preview" className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => removeImage(index)}
                  className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
            {previews.length < 8 && (
              <label className="aspect-square rounded-lg border-2 border-dashed border-accent/30 flex flex-col items-center justify-center cursor-pointer hover:border-accent transition-colors">
                <Upload className="w-6 h-6 text-accent mb-2" />
                <span className="text-[10px] font-bold text-accent uppercase tracking-tighter">Upload</span>
                <input
                  type="file"
                  multiple
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleImageChange}
                  className="hidden"
                />
              </label>
            )}
          </div>
          <p className="text-[10px] text-[#59635C]/60 font-clean uppercase">Max 8 photos. JPG, PNG, WEBP up to 5MB each.</p>
        </div>

        <AnimatePresence>
          {submitError && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="flex items-center gap-2 p-4 bg-red-50 border border-red-100 rounded-xl text-red-600"
            >
              <AlertCircle className="w-5 h-5 shrink-0" />
              <p className="text-sm font-clean font-medium">{submitError}</p>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="flex flex-col sm:flex-row gap-4 pt-4">
          <button
            type="submit"
            disabled={isSubmitting}
            className="flex-1 bg-emerald text-ivory py-4 rounded-full font-clean font-bold uppercase tracking-widest hover:bg-emerald/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Submitting...
              </>
            ) : (
              'Submit Review'
            )}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="sm:w-32 py-4 rounded-full border border-emerald text-emerald font-clean font-bold uppercase tracking-widest hover:bg-emerald/5 transition-all"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};